import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Clock, ShieldCheck, Server } from 'lucide-react';

export const M3UChecker = () => {
    const [url, setUrl] = useState('');
    const [status, setStatus] = useState('idle'); // idle, checking, active, expired, error
    const [result, setResult] = useState(null);

    const checkStatus = async () => {
        if (!url) return;
        setStatus('checking');
        setResult(null);

        try {
            // 1. Basic format validation
            const urlObj = new URL(url);

            // 2. Identify type & Logic to convert get.php to player_api.php
            let targetUrl = url;
            if (url.includes('get.php')) {
                // It's M3U - convert to Player API for metadata
                const params = new URLSearchParams(urlObj.search);
                const u = params.get('username');
                const p = params.get('password');

                // Construct standard Xtreme Codes player_api URL
                if (u && p) {
                    targetUrl = `${urlObj.origin}/player_api.php?username=${u}&password=${p}`;
                }
            }

            // 3. Attempt Fetch via Netlify Proxy (Bypasses CORS)
            // Function path: /.netlify/functions/check-m3u
            const proxyUrl = `/.netlify/functions/check-m3u?url=${encodeURIComponent(targetUrl)}`;

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

            try {
                const response = await fetch(proxyUrl, { signal: controller.signal });
                clearTimeout(timeoutId);

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Server error');
                }

                // If proxy returns successful JSON, validate logic
                validateData(data);

            } catch (networkError) {
                console.warn('Proxy fetch failed:', networkError);
                setStatus('error');
                setResult({ message: 'Unable to connect to server. Check URL or try again.' });
            }

        } catch (e) {
            setStatus('error');
            setResult({ message: 'Invalid URL Format' });
        }
    };

    const validateData = (data) => {
        if (data.user_info) {
            const exp = data.user_info.exp_date;
            // Convert unix
            const expDate = exp ? new Date(exp * 1000) : null;
            const now = new Date();
            const isActive = data.user_info.status === 'Active' && (!expDate || expDate > now);

            setStatus(isActive ? 'active' : 'expired');
            setResult({
                message: data.user_info.message || (isActive ? 'Subscription Active' : 'Subscription Expired'),
                expDate: expDate ? expDate.toLocaleDateString() : 'Unlimited',
                connections: data.user_info.active_cons || 0,
                maxConnections: data.user_info.max_connections || 1
            });
        } else {
            setStatus('error');
            setResult({ message: 'Invalid Response from IPTV Server' });
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-green-500/10 rounded-2xl border border-green-500/20 shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                    <ShieldCheck className="w-8 h-8 text-green-400" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">Subscription Checker</h2>
                    <p className="text-muted-foreground">Verify validity and expiration date instantly.</p>
                </div>
            </div>

            <div className="glass p-6 rounded-2xl border border-white/10 space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">M3U or Player API URL</label>
                    <input
                        type="text"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="http://server.com/get.php?username=XXX&password=XXX"
                        className="w-full px-4 py-4 bg-black/40 border border-white/10 rounded-xl focus:border-green-400/50 focus:ring-1 focus:ring-green-400/50 outline-none transition-all font-mono text-sm"
                    />
                </div>

                <div className="bg-blue-500/5 rounded-lg p-3 border border-blue-500/10 text-xs text-blue-200/80 flex gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    <p>Secure Check: Our server validates the connection. No data is stored.</p>
                </div>

                <button
                    onClick={checkStatus}
                    disabled={status === 'checking' || !url}
                    className="w-full py-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold text-lg transition-all shadow-lg shadow-green-500/20 disabled:opacity-50"
                >
                    {status === 'checking' ? 'Connecting...' : 'Check Status'}
                </button>
            </div>

            {/* Results Display */}
            {(status === 'active' || status === 'expired') && result && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`rounded-2xl border p-6 ${status === 'active' ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
                >
                    <div className="flex items-center gap-4 mb-6">
                        <div className={`p-3 rounded-full ${status === 'active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                            {status === 'active' ? <CheckCircle className="w-8 h-8" /> : <AlertCircle className="w-8 h-8" />}
                        </div>
                        <div>
                            <h3 className="text-xl font-bold">{status === 'active' ? 'Active Subscription' : 'Expired / Inactive'}</h3>
                            <p className="opacity-80">{status === 'active' ? 'Your line is valid and running.' : 'This line cannot be used.'}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="glass bg-black/20 p-4 rounded-xl border border-white/5">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase font-bold mb-1">
                                <Clock className="w-3 h-3" /> Expiration
                            </div>
                            <div className="text-lg font-mono">{result.expDate || 'Unlimited'}</div>
                        </div>
                        <div className="glass bg-black/20 p-4 rounded-xl border border-white/5">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase font-bold mb-1">
                                <Server className="w-3 h-3" /> Connections
                            </div>
                            <div className="text-lg font-mono">{result.connections} / {result.maxConnections}</div>
                        </div>
                    </div>
                </motion.div>
            )}

            {status === 'error' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5" />
                    <p>{result?.message || 'Connection Failed'}</p>
                </motion.div>
            )}
        </div>
    );
};
